import React, { useState, useEffect } from 'react';
import { Button, notification, Spin } from 'antd';
import { sendLetter } from 'Redux/Letter/reducer';
import { useAppDispatch } from 'Redux/store';
import { getCurrentUser } from 'Redux/User/reducer';
import { getOrder } from 'Redux/Order/reducer';
import { useSelector } from 'react-redux';
import { selectCurrentDriver } from 'Redux/Driver/slice';
import { selectCurrentOrder } from 'Redux/Order/slice';
import { selectCurrentUser } from 'Redux/User/slice';
import { selectLetterLoading } from 'Redux/Letter/slice';
import { fillOutHTMLWithVars } from 'Shared/Utils';
import styles from './LetterSendButton.module.scss';

type LetterSendButtonProps = {
  letter: string;
  comment: string;
  commentResetter: () => void;
  brokerPrice: number;
  driverPrice: number;
  buttonName: string;
};

export const LetterSendButton: React.FC<LetterSendButtonProps> = ({
  letter,
  comment,
  commentResetter: resetComment,
  brokerPrice,
  driverPrice,
  buttonName,
}) => {
  const driver = useSelector(selectCurrentDriver);
  const currentOrder = useSelector(selectCurrentOrder);
  const currentUser = useSelector(selectCurrentUser);
  const letterSending = useSelector(selectLetterLoading);

  const [orderIsExpired, setOrderIsExpired] = useState(false);
  const [orderIsPlaced, setOrderIsPlaced] = useState(false);
  const [notificationAPI, contextHolder] = notification.useNotification();
  const dispatch = useAppDispatch();

  const handleBidPlacement = () => {
    if (comment === 'Your comments...') {
      resetComment();
    }

    const letterBody = fillOutHTMLWithVars(letter, {
      comment,
      brokerPrice: brokerPrice,
    });
    const letterHTML = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><title>Bid</title></head><body>${letterBody}</body></html>`;

    dispatch(
      sendLetter({
        driver_id: driver?.driver?.id,
        order_id: currentOrder?.id,
        broker_price: brokerPrice,
        driver_price: driverPrice,
        dispatcher_id: currentUser?.id,
        comment: letterHTML,
      }),
    ).then((action) => {
      if (action.payload) {
        notificationAPI.success({
          message: 'Bid was successfully placed',
          duration: 2,
        });

        if (currentOrder && typeof currentOrder.id === 'number') {
          dispatch(getOrder(currentOrder.id));
        }
        setOrderIsPlaced(true);
      } else {
        notificationAPI.error({
          message: "Internal error, bid wasn't placed",
          duration: 4,
        });
      }
    });
  };

  useEffect(() => {
    let timeoutID: ReturnType<typeof setTimeout>;

    if (currentOrder) {
      if (String(currentOrder.order_status).toLowerCase().includes('await')) {
        setOrderIsPlaced(true);
        return;
      }

      if (!currentUser) {
        dispatch(getCurrentUser());
      }

      const timeoutInterval =
        new Date(currentOrder?.expires).getTime() - new Date().getTime();

      timeoutID = setTimeout(() => {
        setOrderIsExpired(true);
      }, timeoutInterval);
    }

    return () => {
      clearTimeout(timeoutID);
    };
  }, [currentOrder, dispatch]);

  return (
    <>
      {contextHolder}
      <Button
        onClick={handleBidPlacement}
        className={styles.sendButton}
        type={brokerPrice ? 'primary' : undefined}
        disabled={
          letterSending ||
          orderIsExpired ||
          orderIsPlaced ||
          !driver?.driver?.id ||
          !currentOrder?.id ||
          !currentUser?.id ||
          !brokerPrice
        }
        title={
          orderIsExpired
            ? 'It is not possible to place a bid. The order has expired.'
            : orderIsPlaced
            ? 'The order was placed.'
            : driver?.driver?.id && brokerPrice
            ? ''
            : currentOrder?.id && currentUser?.id
            ? 'Choose the driver and fill in the first two rows.'
            : 'Internal error, not enough data to place the bid.'
        }
      >
        {letterSending ? <Spin /> : buttonName}
      </Button>
    </>
  );
};
